/**
 * 模块自动加载器
 * 实现模块的自动注册、路由扫描和菜单聚合
 * @author AI Assistant
 * @date 2024-07-08
 */

import { ref, reactive } from 'vue'
import type { RouteRecordRaw } from 'vue-router'
import type { ModuleConfig, Permission, Role } from '@/types/module-config'
import type { NavigationItem, ModuleNavigation } from '@/types/navigation'
import type { IPermission } from '@/stores/permission'
import { auth } from '@/auth'
import {
  loadAllModuleConfigs,
  generateRoutesFromModuleConfigs,
  generateNavigationFromModuleConfigs,
  generatePermissionsFromModuleConfigs,
  filterRoutesByPermission,
  filterNavigationsByPermission
} from '@/utils/module-config'

/**
 * 模块信息接口
 */
export interface ModuleInfo {
  /** 模块名称 */
  name: string
  /** 模块路径 */
  path: string
  /** 模块配置 */
  config?: ModuleConfig
  /** 路由配置 */
  routes?: RouteRecordRaw[]
}

// 模块状态
const modules = ref<ModuleConfig[]>([])
const routes = ref<RouteRecordRaw[]>([])
const navigations = ref<ModuleNavigation[]>([])
const permissions = ref<IPermission[]>([])
const loading = ref(false)
const error = ref<Error | null>(null)

/**
 * 模块加载器类
 */
export class ModuleLoader {
  private modulesMap = new Map<string, ModuleInfo>()
  private routesList: RouteRecordRaw[] = []
  private navigationsList: ModuleNavigation[] = []

  /**
   * 自动扫描并加载所有模块
   */
  async loadModules(): Promise<void> {
    loading.value = true
    error.value = null

    try {
      // 扫描modules目录下的所有模块配置文件
      const moduleFiles = import.meta.glob('/src/modules/*/config/module.ts', { eager: true })

      // 清空现有模块
      this.modulesMap.clear()
      this.routesList = []
      this.navigationsList = []
      modules.value = []

      // 加载所有模块配置
      for (const [path, module] of Object.entries(moduleFiles)) {
        const moduleName = this.extractModuleName(path)
        if (moduleName) {
          await this.loadModule(moduleName, module as any)
        }
      }

      // 生成全局状态
      modules.value = Array.from(this.modulesMap.values()).map(m => m.config!).filter(Boolean)
      routes.value = generateRoutesFromModuleConfigs(modules.value)
      navigations.value = generateNavigationFromModuleConfigs(modules.value)
      permissions.value = generatePermissionsFromModuleConfigs(modules.value)

      console.log('Module loader initialized successfully', navigations.value)
    } catch (err) {
      console.error('Failed to initialize module loader:', err)
      error.value = err as Error
    } finally {
      loading.value = false
    }
  }

  /**
   * 加载单个模块
   */
  private async loadModule(name: string, moduleConfigModule: any): Promise<void> {
    try {
      const moduleConfig = moduleConfigModule.default || moduleConfigModule

      if (!moduleConfig) {
        throw new Error(`模块 ${name} 配置不存在`)
      }

      const moduleInfo: ModuleInfo = {
        name,
        path: `/src/modules/${name}`,
        config: moduleConfig
      }

      this.modulesMap.set(name, moduleInfo)

    } catch (error) {
      console.error(`模块 ${name} 加载失败:`, error)
    }
  }

  /**
   * 从模块路径提取模块名称
   */
  private extractModuleName(path: string): string | null {
    const match = path.match(/\/src\/modules\/(\w+)\/config\/module\.ts$/)
    return match ? match[1] : null
  }

  /**
   * 根据权限过滤路由和导航
   * @param userPermissions 用户权限
   * @param userRoles 用户角色
   * @returns 过滤后的路由和导航
   */
  filterByPermission(userPermissions: string[] = [], userRoles: string[] = []) {
    // 过滤路由
    const filteredRoutes = filterRoutesByPermission(routes.value, userPermissions, userRoles)

    // 过滤导航
    const filteredNavigations = filterNavigationsByPermission(navigations.value, userPermissions, userRoles)

    return {
      routes: filteredRoutes,
      navigations: filteredNavigations
    }
  }

  /**
   * 获取所有模块
   */
  getModules(): ModuleConfig[] {
    return modules.value
  }

  /**
   * 获取模块路由
   */
  getRoutes(): RouteRecordRaw[] {
    return routes.value
  }

  /**
   * 获取模块导航
   */
  getNavigations(): ModuleNavigation[] {
    return navigations.value.sort((a, b) => (a.order || 0) - (b.order || 0))
  }

  /**
 * 获取模块权限
 */
  getPermissions(): IPermission[] {
    return permissions.value
  }

  /**
 * 根据角色获取权限
 */
  getPermissionsByRoles(roleCodes: string[]): { routePermissions: string[], buttonPermissions: string[], apiPermissions: string[] } {
  // 如果包含admin角色，返回所有权限
    if (roleCodes.includes('admin')) {
      return {
        routePermissions: ['*'],
        buttonPermissions: ['*'],
        apiPermissions: ['*']
      }
    }

    const routePermissions: string[] = []
    const buttonPermissions: string[] = []
    const apiPermissions: string[] = []

    // 获取所有模块配置
    const allModules = modules.value

    // 遍历所有模块
    allModules.forEach(moduleConfig => {
    // 检查模块是否有角色限制
      if (moduleConfig.roles && moduleConfig.roles.length > 0) {
      // 如果用户角色与模块角色有交集，添加该模块的所有权限
        const hasRole = moduleConfig.roles.some(role => roleCodes.includes(role.code))
        if (hasRole) {
        // 添加路由权限
          if (moduleConfig.routes) {
            moduleConfig.routes.forEach(route => {
              if (route.meta?.permission) {
                routePermissions.push(route.meta.permission)
              }
            })
          }

          // 添加按钮权限
          if (moduleConfig.permissions) {
            moduleConfig.permissions.forEach(perm => {
              if (perm.type === 'button') {
                buttonPermissions.push(perm.code)
              } else if (perm.type === 'api') {
                apiPermissions.push(perm.code)
              }
            })
          }
        }
      }
    })

    return {
      routePermissions,
      buttonPermissions,
      apiPermissions
    }
  }

  /**
   * 获取模块角色
   */
  getRoles(): Role[] {
    // 从模块配置中提取所有角色
    const roleMap = new Map<string, Role>()
    modules.value.forEach(config => {
      if (config.roles && config.roles.length > 0) {
        config.roles.forEach(role => {
          if (!roleMap.has(role.code)) {
            roleMap.set(role.code, role)
          }
        })
      }
    })
    return Array.from(roleMap.values())
  }

  /**
   * 获取指定模块
   */
  getModule(name: string): ModuleInfo | undefined {
    return this.modulesMap.get(name)
  }

  /**
   * 重新加载模块
   */
  async reloadModule(name: string): Promise<void> {
    this.modulesMap.delete(name)
    // 重新加载逻辑
    await this.loadModules()
  }

  /**
   * 获取加载状态
   */
  getLoadingState() {
    return {
      loading: loading.value,
      error: error.value
    }
  }

  /**
   * 启用模块
   * @param moduleId 模块ID
   */
  enableModule(moduleId: string) {
    const module = modules.value.find(m => m.id === moduleId)
    if (module) {
      module.enabled = true
      // 重新生成路由和导航
      routes.value = generateRoutesFromModuleConfigs(modules.value.filter(m => m.enabled !== false))
      navigations.value = generateNavigationFromModuleConfigs(modules.value.filter(m => m.enabled !== false))
    }
  }

  /**
   * 禁用模块
   * @param moduleId 模块ID
   */
  disableModule(moduleId: string) {
    const module = modules.value.find(m => m.id === moduleId)
    if (module) {
      module.enabled = false
      // 重新生成路由和导航
      routes.value = generateRoutesFromModuleConfigs(modules.value.filter(m => m.enabled !== false))
      navigations.value = generateNavigationFromModuleConfigs(modules.value.filter(m => m.enabled !== false))
    }
  }
}

// 导出单例实例
export const moduleLoader = new ModuleLoader()

// 导出辅助函数
export const initModuleLoader = () => moduleLoader.loadModules()
export const getModules = () => moduleLoader.getModules()
export const getRoutes = () => moduleLoader.getRoutes()
export const getNavigations = () => moduleLoader.getNavigations()
export const getPermissions = () => moduleLoader.getPermissions()
export const getRoles = () => moduleLoader.getRoles()
export const getPermissionsByRoles = (roleCodes: string[]) => moduleLoader.getPermissionsByRoles(roleCodes)
export const getLoadingState = () => moduleLoader.getLoadingState()
export const filterByPermission = (permissions: string[], roles: string[]) =>
  moduleLoader.filterByPermission(permissions, roles)
export const enableModule = (moduleId: string) => moduleLoader.enableModule(moduleId)
export const disableModule = (moduleId: string) => moduleLoader.disableModule(moduleId)

export default {
  moduleLoader,
  initModuleLoader,
  getModules,
  getRoutes,
  getNavigations,
  getPermissions,
  getRoles,
  getLoadingState,
  filterByPermission,
  enableModule,
  disableModule
}
